import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeeFormModal } from '@/components/ui/tee-form-modal';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeTextArea } from '@/components/ui/tee-textarea';
import { TeeTagInput } from '@/components/ui/tee-tag-input';
import { TeeMultiSelect } from '@/components/ui/tee-multi-select';
import { TeeTabs, TeeTabsList, TeeTabsTrigger, TeeTabsContent } from '@/components/ui/tee-tabs';
import { TeeDropZone } from '@/components/ui/tee-drop-zone';
import { useTaxonomy } from '@/hooks/useTaxonomy';
import { useContentItems } from '@/hooks/useContentItems';
import { contentItemService } from '@/services/content-item.service';
import { taxonomyService } from '@/services/taxonomy.service';
import { showErrorToast, showSuccessToast } from '@/utils/toast.utils';
import { TaxonomyType } from '@/types/taxonomy';
import {
  ContentItem,
  ContentItemFormData,
  ContentMediaType,
  EmbedProvider,
} from '@/types/contentItem';

interface ContentItemFormModalProps {
  isOpen: boolean;
  editTarget: ContentItem | null;
  onClose: () => void;
  onSaved: () => void;
}

// Embed URL validation (D-02, T-314-16). Parses with `new URL()`, requires
// https, and matches the hostname by EXACT equality against an allow-list —
// never a substring check, which `youtube.com.evil.tld` would defeat.
// The mytpi and "other" provider values are deliberately not accepted here —
// CONTENT-03 scopes this phase to YouTube and Vimeo only.
function deriveEmbedProvider(url: string): EmbedProvider | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  const hostname = parsed.hostname.replace(/^www\./, '');
  if (hostname === 'youtube.com' || hostname === 'm.youtube.com' || hostname === 'youtu.be') {
    return EmbedProvider.YOUTUBE;
  }
  if (hostname === 'vimeo.com' || hostname === 'player.vimeo.com') {
    return EmbedProvider.VIMEO;
  }
  return null;
}

export default function ContentItemFormModal({
  isOpen,
  editTarget,
  onClose,
  onSaved,
}: ContentItemFormModalProps) {
  const { t } = useTranslation();
  const {
    options: taxonomyOptions,
    loading: taxonomyLoading,
    allowInlineCreate,
  } = useTaxonomy();
  const { createContentItem, updateContentItem } = useContentItems();

  // Shared metadata form state — identical across all three media types (D-03).
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [archetypeIds, setArchetypeIds] = useState<string[]>([]);
  const [tpiScreenIds, setTpiScreenIds] = useState<string[]>([]);
  const [sbGoalIds, setSbGoalIds] = useState<string[]>([]);
  const [equipmentIds, setEquipmentIds] = useState<string[]>([]);
  const [patternIds, setPatternIds] = useState<string[]>([]);
  const [positionIds, setPositionIds] = useState<string[]>([]);
  const [sets, setSets] = useState('');
  const [reps, setReps] = useState('');
  const [frequency, setFrequency] = useState('');
  const [intensity, setIntensity] = useState('');
  const [titleError, setTitleError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Media type + per-source state (D-02) — exactly one source is populated
  // at a time, enforced by handleMediaTypeChange below.
  const [mediaType, setMediaType] = useState<ContentMediaType>(ContentMediaType.IMAGE);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);

  // Prefill from editTarget or reset to blank — single effect makes this
  // modal work identically for create, metadata edit, and media replace (D-05).
  useEffect(() => {
    if (editTarget) {
      setTitle(editTarget.title ?? '');
      setDescription(editTarget.description ?? '');
      setCategoryIds(editTarget.categoryIds ?? []);
      setTags(editTarget.tags ?? []);
      setArchetypeIds(editTarget.archetypeIds ?? []);
      setTpiScreenIds(editTarget.tpiScreenIds ?? []);
      setSbGoalIds(editTarget.sbGoalIds ?? []);
      setEquipmentIds(editTarget.equipmentIds ?? []);
      setPatternIds(editTarget.patternIds ?? []);
      setPositionIds(editTarget.positionIds ?? []);
      setSets(editTarget.sets ?? '');
      setReps(editTarget.reps ?? '');
      setFrequency(editTarget.frequency ?? '');
      setIntensity(editTarget.intensity ?? '');

      setMediaType(editTarget.mediaType);
      if (editTarget.mediaType === ContentMediaType.IMAGE) {
        setPreviewUrl(editTarget.imageSignedUrl ?? editTarget.imageUrl ?? null);
      } else if (editTarget.mediaType === ContentMediaType.VIDEO) {
        setPreviewUrl(editTarget.videoThumbnailUrl ?? null);
      } else {
        setPreviewUrl(null);
      }
      setEmbedUrl(editTarget.mediaType === ContentMediaType.EMBED ? editTarget.embedUrl ?? '' : '');
    } else {
      setTitle('');
      setDescription('');
      setCategoryIds([]);
      setTags([]);
      setArchetypeIds([]);
      setTpiScreenIds([]);
      setSbGoalIds([]);
      setEquipmentIds([]);
      setPatternIds([]);
      setPositionIds([]);
      setSets('');
      setReps('');
      setFrequency('');
      setIntensity('');

      setMediaType(ContentMediaType.IMAGE);
      setPreviewUrl(null);
      setEmbedUrl('');
    }
    setTitleError(null);
    setImageFile(null);
    setVideoFile(null);
    setMediaError(null);
    setEmbedError(null);
  }, [editTarget, isOpen]);

  // Revoke the outstanding blob preview URL whenever it changes (tab switch,
  // new file selection, clear) and on unmount — prevents blob URLs leaking
  // across repeated modal opens.
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Local mutable copy of taxonomyOptions so a freshly-created value shows up
  // immediately without a refetch (see handleCreateTaxonomyOption below).
  const [localTaxonomyOptions, setLocalTaxonomyOptions] = useState(taxonomyOptions);
  useEffect(() => setLocalTaxonomyOptions(taxonomyOptions), [taxonomyOptions]);
  const [creatingTaxonomy, setCreatingTaxonomy] = useState<TaxonomyType | null>(null);

  const handleCreateTaxonomyOption = async (
    type: TaxonomyType,
    setIds: React.Dispatch<React.SetStateAction<string[]>>,
    label: string
  ) => {
    setCreatingTaxonomy(type);
    try {
      const created = await taxonomyService.findOrCreate(type, label);
      setLocalTaxonomyOptions((prev) => ({
        ...prev,
        [type]: prev[type].some((o) => o.value === created.id)
          ? prev[type]
          : [...prev[type], { label: created.value, value: created.id }],
      }));
      setIds((prev) => (prev.includes(created.id) ? prev : [...prev, created.id]));
    } catch {
      showErrorToast(t('contentLibrary.errorCreateTaxonomyOption'));
    } finally {
      setCreatingTaxonomy(null);
    }
  };

  // Switching the active media tab clears every other media source (D-02) —
  // only the selected tab's source is ever staged for submit.
  const handleMediaTypeChange = (nextValue: string) => {
    const next = nextValue as ContentMediaType;
    if (next === mediaType) return;
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageFile(null);
    setVideoFile(null);
    setPreviewUrl(null);
    setEmbedUrl('');
    setMediaError(null);
    setEmbedError(null);
    setMediaType(next);
  };

  const handleImageSelected = (file: File) => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setMediaError(null);
  };

  const handleImageCleared = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageFile(null);
    setPreviewUrl(null);
  };

  const handleVideoSelected = (file: File) => {
    setVideoFile(file);
    setMediaError(null);
  };

  const handleVideoCleared = () => {
    setVideoFile(null);
  };

  const handleEmbedUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmbedUrl(e.target.value);
    if (embedError) setEmbedError(null);
  };

  const handleEmbedUrlBlur = () => {
    if (!embedUrl) {
      setEmbedError(null);
      return;
    }
    setEmbedError(deriveEmbedProvider(embedUrl) ? null : t('contentLibrary.errorEmbedUrl'));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      // No dedicated i18n key exists for this validation message
      // (contentLibrary.errorTitleRequired) — en.json is single-plan-owned
      // for phase 314 (314-03) and this plan must not add keys. Flagged in
      // the plan summary for a future i18n pass.
      setTitleError('Title is required.');
      return;
    }

    let embedProvider: EmbedProvider | null = null;
    if (mediaType === ContentMediaType.EMBED) {
      embedProvider = deriveEmbedProvider(embedUrl);
      if (!embedProvider) {
        setEmbedError(t('contentLibrary.errorEmbedUrl'));
        return;
      }
    }

    // Server-owned fields (status, imageUrl, gumletAssetId,
    // videoProcessingStatus, publishedAt, createdBy, updatedBy) are never
    // included here — the backend whitelist strips them anyway (T-314-17).
    const data: ContentItemFormData = {
      title: title.trim(),
      mediaType,
      categoryIds,
      tags,
      archetypeIds,
      tpiScreenIds,
      sbGoalIds,
      equipmentIds,
      patternIds,
      positionIds,
    };
    if (description.trim()) data.description = description.trim();
    if (sets.trim()) data.sets = sets.trim();
    if (reps.trim()) data.reps = reps.trim();
    if (frequency.trim()) data.frequency = frequency.trim();
    if (intensity.trim()) data.intensity = intensity.trim();
    if (mediaType === ContentMediaType.EMBED && embedProvider) {
      data.embedUrl = embedUrl.trim();
      data.embedProvider = embedProvider;
    }

    setIsSubmitting(true);
    try {
      // Phase 1 — always awaited: create or update the item's metadata.
      let id: string;
      if (editTarget) {
        await updateContentItem(editTarget.id, data);
        id = editTarget.id;
      } else {
        const created = await createContentItem(data);
        id = created.id;
      }

      // Phase 2 — media, branching per D-04.
      if (mediaType === ContentMediaType.IMAGE && imageFile) {
        try {
          await contentItemService.uploadContentItemImage(id, imageFile);
        } catch {
          showErrorToast(t('contentLibrary.errorSaveItem'));
          setIsSubmitting(false);
          return;
        }
      } else if (mediaType === ContentMediaType.VIDEO && videoFile) {
        let uploadUrl: string;
        try {
          const target = await contentItemService.requestVideoUploadUrl(id);
          uploadUrl = target.uploadUrl;
        } catch {
          showErrorToast(t('contentLibrary.errorVideoUpload'));
          setIsSubmitting(false);
          return;
        }
        // Fire-and-forget (D-04) — the modal closes immediately; the
        // table's videoProcessingStatus badge communicates progress.
        contentItemService.putVideoFile(uploadUrl, videoFile).catch(() => {
          showErrorToast(t('contentLibrary.errorVideoUpload'));
        });
      }

      showSuccessToast(t(editTarget ? 'contentLibrary.itemUpdated' : 'contentLibrary.itemCreated'));
      onSaved();
      onClose();
    } catch {
      showErrorToast(t('contentLibrary.errorSaveItem'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TeeFormModal
      isOpen={isOpen}
      title={editTarget ? t('contentLibrary.modalTitleEdit') : t('contentLibrary.modalTitleCreate')}
      onClose={onClose}
      onPrimaryAction={handleSave}
      isLoading={isSubmitting}
      primaryActionLabel={editTarget ? t('contentLibrary.saveChanges') : t('contentLibrary.createItem')}
      secondaryActionLabel={t('common.cancel')}
      className="w-[600px] max-w-[600px]"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <TeeInput
            label={`${t('contentLibrary.fieldTitle')} *`}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError(null);
            }}
            destructive={!!titleError}
            hint={titleError ?? undefined}
          />

          <TeeTextArea
            label={t('contentLibrary.fieldDescription')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />

          <TeeTagInput
            values={tags}
            onChange={setTags}
            label={t('contentLibrary.fieldTags')}
            hint={t('contentLibrary.tagsHint')}
          />
        </div>

        <div className="flex flex-col gap-4">
          <TeeMultiSelect
            options={localTaxonomyOptions[TaxonomyType.CATEGORY]}
            values={categoryIds}
            onChange={setCategoryIds}
            label={t('contentLibrary.fieldCategory')}
            disabled={taxonomyLoading}
            allowCreate={allowInlineCreate[TaxonomyType.CATEGORY] ?? true}
            creating={creatingTaxonomy === TaxonomyType.CATEGORY}
            onCreateOption={(label) =>
              handleCreateTaxonomyOption(TaxonomyType.CATEGORY, setCategoryIds, label)
            }
            createOptionLabel={(term) => t('contentLibrary.createTaxonomyOption', { term })}
          />
          <TeeMultiSelect
            options={localTaxonomyOptions[TaxonomyType.ARCHETYPE]}
            values={archetypeIds}
            onChange={setArchetypeIds}
            label={t('contentLibrary.fieldArchetype')}
            disabled={taxonomyLoading}
            allowCreate={allowInlineCreate[TaxonomyType.ARCHETYPE] ?? true}
            creating={creatingTaxonomy === TaxonomyType.ARCHETYPE}
            onCreateOption={(label) =>
              handleCreateTaxonomyOption(TaxonomyType.ARCHETYPE, setArchetypeIds, label)
            }
            createOptionLabel={(term) => t('contentLibrary.createTaxonomyOption', { term })}
          />
          <TeeMultiSelect
            options={localTaxonomyOptions[TaxonomyType.TPI_SCREEN]}
            values={tpiScreenIds}
            onChange={setTpiScreenIds}
            label={t('contentLibrary.fieldTpiScreen')}
            disabled={taxonomyLoading}
            allowCreate={allowInlineCreate[TaxonomyType.TPI_SCREEN] ?? true}
            creating={creatingTaxonomy === TaxonomyType.TPI_SCREEN}
            onCreateOption={(label) =>
              handleCreateTaxonomyOption(TaxonomyType.TPI_SCREEN, setTpiScreenIds, label)
            }
            createOptionLabel={(term) => t('contentLibrary.createTaxonomyOption', { term })}
          />
          <TeeMultiSelect
            options={localTaxonomyOptions[TaxonomyType.SB_GOAL]}
            values={sbGoalIds}
            onChange={setSbGoalIds}
            label={t('contentLibrary.fieldSbGoal')}
            disabled={taxonomyLoading}
            allowCreate={allowInlineCreate[TaxonomyType.SB_GOAL] ?? true}
            creating={creatingTaxonomy === TaxonomyType.SB_GOAL}
            onCreateOption={(label) =>
              handleCreateTaxonomyOption(TaxonomyType.SB_GOAL, setSbGoalIds, label)
            }
            createOptionLabel={(term) => t('contentLibrary.createTaxonomyOption', { term })}
          />
          <TeeMultiSelect
            options={localTaxonomyOptions[TaxonomyType.EQUIPMENT]}
            values={equipmentIds}
            onChange={setEquipmentIds}
            label={t('contentLibrary.fieldEquipment')}
            disabled={taxonomyLoading}
            allowCreate={allowInlineCreate[TaxonomyType.EQUIPMENT] ?? true}
            creating={creatingTaxonomy === TaxonomyType.EQUIPMENT}
            onCreateOption={(label) =>
              handleCreateTaxonomyOption(TaxonomyType.EQUIPMENT, setEquipmentIds, label)
            }
            createOptionLabel={(term) => t('contentLibrary.createTaxonomyOption', { term })}
          />
          <TeeMultiSelect
            options={localTaxonomyOptions[TaxonomyType.PATTERN]}
            values={patternIds}
            onChange={setPatternIds}
            label={t('contentLibrary.fieldPattern')}
            disabled={taxonomyLoading}
            allowCreate={allowInlineCreate[TaxonomyType.PATTERN] ?? true}
            creating={creatingTaxonomy === TaxonomyType.PATTERN}
            onCreateOption={(label) =>
              handleCreateTaxonomyOption(TaxonomyType.PATTERN, setPatternIds, label)
            }
            createOptionLabel={(term) => t('contentLibrary.createTaxonomyOption', { term })}
          />
          <TeeMultiSelect
            options={localTaxonomyOptions[TaxonomyType.POSITION]}
            values={positionIds}
            onChange={setPositionIds}
            label={t('contentLibrary.fieldPosition')}
            disabled={taxonomyLoading}
            allowCreate={allowInlineCreate[TaxonomyType.POSITION] ?? true}
            creating={creatingTaxonomy === TaxonomyType.POSITION}
            onCreateOption={(label) =>
              handleCreateTaxonomyOption(TaxonomyType.POSITION, setPositionIds, label)
            }
            createOptionLabel={(term) => t('contentLibrary.createTaxonomyOption', { term })}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <TeeInput
            label={t('contentLibrary.fieldSets')}
            value={sets}
            onChange={(e) => setSets(e.target.value)}
          />
          <TeeInput
            label={t('contentLibrary.fieldReps')}
            value={reps}
            onChange={(e) => setReps(e.target.value)}
          />
          <TeeInput
            label={t('contentLibrary.fieldFrequency')}
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
          />
          <TeeInput
            label={t('contentLibrary.fieldIntensity')}
            value={intensity}
            onChange={(e) => setIntensity(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-4">
          <TeeTabs value={mediaType} onChange={handleMediaTypeChange}>
            <TeeTabsList>
              <TeeTabsTrigger value={ContentMediaType.IMAGE}>
                {t('contentLibrary.mediaTabImage')}
              </TeeTabsTrigger>
              <TeeTabsTrigger value={ContentMediaType.VIDEO}>
                {t('contentLibrary.mediaTabVideoUpload')}
              </TeeTabsTrigger>
              <TeeTabsTrigger value={ContentMediaType.EMBED}>
                {t('contentLibrary.mediaTabVideoLink')}
              </TeeTabsTrigger>
            </TeeTabsList>

            <TeeTabsContent value={ContentMediaType.IMAGE}>
              <TeeDropZone
                accept="image/png,image/jpeg,image/webp"
                maxSizeMb={10}
                file={imageFile}
                previewUrl={previewUrl}
                onFileSelected={handleImageSelected}
                onFileCleared={handleImageCleared}
                onError={setMediaError}
                idleText={t('contentLibrary.dropZoneIdle')}
                hint={t('contentLibrary.dropZoneImageHint')}
                browseText={t('contentLibrary.dropZoneBrowse')}
                removeText={t('contentLibrary.dropZoneRemove')}
              />
            </TeeTabsContent>

            <TeeTabsContent value={ContentMediaType.VIDEO}>
              <TeeDropZone
                accept="video/mp4,video/quicktime"
                maxSizeMb={2048}
                file={videoFile}
                previewUrl={previewUrl}
                onFileSelected={handleVideoSelected}
                onFileCleared={handleVideoCleared}
                onError={setMediaError}
                idleText={t('contentLibrary.dropZoneIdle')}
                hint={t('contentLibrary.dropZoneVideoHint')}
                browseText={t('contentLibrary.dropZoneBrowse')}
                removeText={t('contentLibrary.dropZoneRemove')}
              />
            </TeeTabsContent>

            <TeeTabsContent value={ContentMediaType.EMBED}>
              <TeeInput
                label={t('contentLibrary.fieldVideoUrl')}
                hint={embedError ?? t('contentLibrary.videoUrlHint')}
                value={embedUrl}
                onChange={handleEmbedUrlChange}
                onBlur={handleEmbedUrlBlur}
                destructive={!!embedError}
                type="url"
              />
            </TeeTabsContent>
          </TeeTabs>

          {mediaError && (
            <p className="text-body-xs font-medium text-red-500">{mediaError}</p>
          )}
        </div>
      </div>
    </TeeFormModal>
  );
}
